import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/integrations/supabase/client.safe'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Progress } from '@/components/ui/progress'
import { Switch } from '@/components/ui/switch'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { 
  RefreshCw, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  XCircle, 
  Play, 
  Square, 
  RotateCcw, 
  ExternalLink, 
  Coins,
  Timer,
  Users,
  Building2
} from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { QueueFilters } from '@/components/QueueFilters'
import { JobActionsMenu } from '@/components/JobActionsMenu'
import { ErrorBadge } from '@/components/ErrorLogPanel'
import { QueueEmptyState } from '@/components/QueueEmptyState'
import { formatWithUTC } from '@/lib/datetime'

interface Job {
  id: string
  job_type: string
  status: string
  priority: number
  audit_id: string
  user_id: string
  created_at: string
  started_at?: string
  completed_at?: string
  attempt_count: number
  max_attempts: number
  error_message?: string
  error_details?: any
  deployment_name?: string
  tokens_used?: number
  estimated_cost?: number
  created_by_name?: string
  case_title?: string
}

interface Organization {
  id: string
  name: string
}

interface UserProfile {
  id: string
  full_name?: string
}

interface QueuePageProps {
  organization: Organization | null
  profile: UserProfile | null
}

const statusColors = {
  queued: 'bg-warning/10 text-warning border-warning/20',
  running: 'bg-primary/10 text-primary border-primary/20',
  completed: 'bg-success/10 text-success border-success/20',
  failed: 'bg-destructive/10 text-destructive border-destructive/20'
}

const statusIcons = {
  queued: Clock,
  running: Play,
  completed: CheckCircle,
  failed: XCircle
}

const jobTypeLabels = {
  'analyze_documents': 'Dokumentanalys',
  'generate_report': 'Rapportgenerering',
  'extract_data': 'Dataextrahering',
  'compliance_check': 'Compliance-kontroll'
}

export function QueuePage({ organization, profile }: QueuePageProps) {
  const { t } = useTranslation()
  const [jobs, setJobs] = useState<Job[]>([])
  const [filteredJobs, setFilteredJobs] = useState<Job[]>([])
  const [loading, setLoading] = useState(true)
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout | null>(null)
  const [activeStatusFilter, setActiveStatusFilter] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const { toast } = useToast()

  // Status labels using translations
  const statusLabels = {
    queued: t('queue.status.queued'),
    running: t('queue.status.running'), 
    completed: t('queue.status.completed'),
    failed: t('queue.status.failed')
  }

  // Calculate queue statistics
  const calculateQueueStats = useCallback(() => {
    return jobs.reduce((acc, job) => {
      acc[job.status] = (acc[job.status] || 0) + 1
      return acc
    }, {} as Record<string, number>)
  }, [jobs])

  // Filter jobs based on status and search
  useEffect(() => {
    let filtered = jobs

    if (activeStatusFilter) {
      filtered = filtered.filter(job => job.status === activeStatusFilter)
    }

    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase()
      filtered = filtered.filter(job => 
        job.id.toLowerCase().includes(search) ||
        job.audit_id.toLowerCase().includes(search) ||
        job.job_type.toLowerCase().includes(search) ||
        job.case_title?.toLowerCase().includes(search) ||
        job.created_by_name?.toLowerCase().includes(search)
      )
    }

    setFilteredJobs(filtered)
  }, [jobs, activeStatusFilter, searchTerm])

  // Auto-refresh functionality
  useEffect(() => {
    if (autoRefresh) {
      fetchJobs()
      const interval = setInterval(fetchJobs, 15000) // Refresh every 15 seconds
      setRefreshInterval(interval)
      return () => clearInterval(interval)
    } else {
      if (refreshInterval) {
        clearInterval(refreshInterval)
        setRefreshInterval(null)
      }
    }
  }, [autoRefresh])

  const fetchJobs = async () => {
    try {
      // Simple query without joins for now - we'll enhance when the relations exist
      const { data, error } = await supabase
        .from('jobs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100)

      if (error) throw error
      
      // Transform real jobs data
      const transformedJobs = (data || []).map(job => ({
        ...job,
        case_title: `Ärende ${job.audit_id?.slice(0, 8) || 'N/A'}`,
        created_by_name: 'Systemanvändare',
        deployment_name: `prod-${job.job_type.split('_')[0]}`,
        tokens_used: Math.floor(Math.random() * 5000) + 1000, // Mock tokens
        estimated_cost: Number((Math.random() * 2 + 0.1).toFixed(3)) // Mock cost as number
      }))
      
      setJobs(transformedJobs)
    } catch (error) {
      console.error('Error fetching jobs:', error)
      toast({
        title: t("queue.toast.error"),
        description: t("queue.toast.errorDesc"),
        variant: "destructive"
      })
    } finally {
      setLoading(false)
    }
  }

  const retryJob = async (jobId: string) => {
    try {
      // TODO: Implement actual retry logic via edge function
      const { error } = await supabase
        .from('jobs')
        .update({ status: 'queued', error_message: null })
        .eq('id', jobId)
      
      if (error) throw error
      
      toast({
        title: "Försöker igen",
        description: "Jobbet har satts i kö igen"
      })
      fetchJobs()
    } catch (error) {
      console.error('Error retrying job:', error)
      toast({
        title: "Fel",
        description: "Kunde inte köra jobbet igen",
        variant: "destructive"
      })
    }
  }

  const cancelJob = async (jobId: string) => {
    try {
      // TODO: Implement actual cancel logic via edge function
      const { error } = await supabase
        .from('jobs')
        .update({ status: 'failed', error_message: 'Avbrutet av användare' })
        .eq('id', jobId)
        
      if (error) throw error
      
      toast({
        title: "Avbrutet",
        description: "Jobbet har avbrutits"
      })
      fetchJobs()
    } catch (error) {
      console.error('Error canceling job:', error)
      toast({
        title: "Fel", 
        description: "Kunde inte avbryta jobbet",
        variant: "destructive"
      })
    }
  }

  const changePriority = async (jobId: string, newPriority: number) => {
    try {
      const { error } = await supabase
        .from('jobs')
        .update({ priority: newPriority })
        .eq('id', jobId)
        
      if (error) throw error
      fetchJobs()
    } catch (error) {
      console.error('Error changing priority:', error)
      toast({
        title: "Fel",
        description: "Kunde inte ändra prioritet",
        variant: "destructive"
      })
    }
  }

  // Removed - using formatWithUTC from @/lib/datetime

  const formatDuration = (startDate: string, endDate?: string) => {
    const start = new Date(startDate)
    const end = endDate ? new Date(endDate) : new Date()
    const diff = end.getTime() - start.getTime()
    
    const minutes = Math.floor(diff / (1000 * 60))
    const seconds = Math.floor((diff % (1000 * 60)) / 1000)
    
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`
    }
    return `${seconds}s`
  }

  // Calculate ETA and queue position
  const calculateETA = (job: Job) => {
    if (job.status === 'queued') {
      const queuedJobs = jobs.filter(j => j.status === 'queued')
      const position = queuedJobs.findIndex(j => j.id === job.id) + 1
      
      // Mock ETA calculation based on average job duration (3 minutes per job)
      const avgDurationMinutes = 3
      const etaMinutes = position * avgDurationMinutes
      
      return {
        position,
        etaMinutes,
        etaText: etaMinutes < 60 
          ? `~${etaMinutes} min` 
          : `~${Math.round(etaMinutes / 60)}h ${etaMinutes % 60}min`,
        isRunning: false
      }
    }
    
    // ETA for running jobs based on progress
    if (job.status === 'running' && job.started_at) {
      const startTime = new Date(job.started_at).getTime()
      const elapsed = Date.now() - startTime
      const estimatedDuration = 3 * 60 * 1000 // 3 minutes
      const remaining = Math.max(0, estimatedDuration - elapsed)
      const remainingMinutes = Math.ceil(remaining / (1000 * 60))
      
      return {
        position: 0,
        etaMinutes: remainingMinutes,
        etaText: remainingMinutes > 0 ? `~${remainingMinutes} min kvar` : 'Avslutas snart',
        isRunning: true
      }
    }
    
    return null
  }

  const getProgress = (job: Job) => {
    if (job.status === 'completed') return 100
    if (job.status === 'failed') return 100
    if (job.status === 'running') {
      // Better progress simulation based on job duration
      const startTime = job.started_at ? new Date(job.started_at).getTime() : Date.now()
      const elapsed = Date.now() - startTime
      const estimatedDuration = 3 * 60 * 1000 // 3 minutes
      return Math.min(90, (elapsed / estimatedDuration) * 100)
    }
    return 0
  }

  const stats = calculateQueueStats()

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <TooltipProvider>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('queue.title.page')}</h1>
          <p className="text-muted-foreground">{t('queue.subtitle')}</p>
        </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Switch 
                checked={autoRefresh} 
                onCheckedChange={setAutoRefresh}
                id="auto-refresh"
              />
              <label htmlFor="auto-refresh" className="text-sm text-muted-foreground">
                {t('queue.autoRefresh.label', { seconds: '15' })}
              </label>
            </div>
            <Button onClick={fetchJobs} variant="outline" className="gap-2">
              <RefreshCw className="h-4 w-4" />
              {t('queue.refresh')}
            </Button>
          </div>
        </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('queue.stats.queued')}</CardTitle>
            <Clock className="h-4 w-4 text-warning" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-warning">{stats.queued || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('queue.stats.running')}</CardTitle>
            <Play className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">{stats.running || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('queue.stats.completed')}</CardTitle>
            <CheckCircle className="h-4 w-4 text-success" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-success">{stats.completed || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('queue.stats.failed')}</CardTitle>
            <XCircle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">{stats.failed || 0}</div>
          </CardContent>
        </Card>
      </div>

        {/* Filters */}
        <QueueFilters
          onStatusFilter={setActiveStatusFilter}
          onSearchFilter={setSearchTerm}
          activeStatus={activeStatusFilter}
          searchTerm={searchTerm}
          statusCounts={stats}
        />

        {/* Jobs Table or Empty State */}
        {filteredJobs.length === 0 && jobs.length === 0 ? (
          <QueueEmptyState />
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>
                  Jobb {activeStatusFilter && `(${statusLabels[activeStatusFilter]})`}
                </span>
                <Badge variant="outline">
                  {filteredJobs.length} av {jobs.length}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Jobbtyp & ID</TableHead>
                    <TableHead>Ärende</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Framsteg & ETA</TableHead>
                    <TableHead>Deployment</TableHead>
                    <TableHead>Skapad av</TableHead>
                    <TableHead>Resursanvändning</TableHead>
                    <TableHead className="text-right">Åtgärder</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredJobs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                        {searchTerm ? `Inga jobb matchade "${searchTerm}"` : 'Inga jobb med vald status'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredJobs.map((job) => {
                      const StatusIcon = statusIcons[job.status]
                      const progress = getProgress(job)
                      const eta = calculateETA(job)
                      
                      return (
                        <TableRow key={job.id}>
                          <TableCell>
                            <div className="font-medium">
                              {jobTypeLabels[job.job_type as keyof typeof jobTypeLabels] || job.job_type}
                            </div>
                            <div className="text-sm text-muted-foreground font-mono">
                              {job.id.slice(0, 8)}...
                            </div>
                            <Badge variant="outline" className="mt-1">P{job.priority}</Badge>
                          </TableCell>
                          
                          <TableCell>
                            {job.case_title ? (
                              <Link 
                                to={`/cases/${job.audit_id}`}
                                className="text-primary hover:underline"
                              >
                                <div className="font-medium">{job.case_title}</div>
                                <div className="text-xs text-muted-foreground flex items-center gap-1">
                                  <ExternalLink className="h-3 w-3" />
                                  Öppna ärende
                                </div>
                              </Link>
                            ) : (
                              <span className="text-muted-foreground italic">Okänt ärende</span>
                            )}
                          </TableCell>
                          
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <StatusIcon className="h-4 w-4" />
                              <Badge className={statusColors[job.status]}>
                                {statusLabels[job.status]}
                              </Badge>
                            </div>
                            <ErrorBadge job={job} />
                          </TableCell>
                          
                          <TableCell>
                            <div className="space-y-1">
                              <Progress value={progress} className="w-full" />
                              <div className="text-xs text-muted-foreground">
                                {progress.toFixed(0)}%
                              </div>
                              {eta && (
                                <div className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Timer className="h-3 w-3" />
                                  Pos {eta.position}, ETA {eta.etaText}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          
                          <TableCell>
                            <div className="text-sm">
                              <div className="font-mono">{job.deployment_name}</div>
                              <div className="text-xs text-muted-foreground">
                                {job.attempt_count}/{job.max_attempts} försök
                              </div>
                            </div>
                          </TableCell>
                          
                          <TableCell>
                            <div className="text-sm">
                              <div className="flex items-center gap-1">
                                <Users className="h-3 w-3" />
                                {job.created_by_name || 'Okänd'}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {formatWithUTC(job.created_at)}
                              </div>
                            </div>
                          </TableCell>
                          
                          <TableCell>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="text-xs cursor-help">
                                  <div className="flex items-center gap-1">
                                    <Coins className="h-3 w-3" />
                                    {job.tokens_used?.toLocaleString() || 'N/A'} tokens
                                  </div>
                                  <div className="text-muted-foreground">
                                    ~{job.estimated_cost} SEK
                                  </div>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>
                                <div className="text-xs">
                                  <div>Tokens: {job.tokens_used?.toLocaleString()}</div>
                                  <div>Beräknad kostnad: {job.estimated_cost} SEK</div>
                                  <div>Modell: GPT-4 Turbo</div>
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TableCell>
                          
                          <TableCell className="text-right">
                            <JobActionsMenu
                              job={job}
                              onRetry={retryJob}
                              onCancel={cancelJob}
                              onChangePriority={changePriority}
                            />
                          </TableCell>
                        </TableRow>
                      )
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
    </TooltipProvider>

  )
}